import React, { useState, useEffect } from 'react';
import { Box, Typography, Grid, Chip } from '@mui/material';
import { TimerOutlined, CheckCircleOutline } from '@mui/icons-material';

// --- Вспомогательные функции для форматирования времени ---

/**
 * Форматирует продолжительность из миллисекунд в строку "Xч Yм Zс"
 * @param {number} milliseconds - Продолжительность в миллисекундах
 */
function formatDuration(milliseconds) {
  if (isNaN(milliseconds) || milliseconds < 0) {
    return '0м 0с';
  }

  const totalSeconds = Math.floor(milliseconds / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  let result = '';
  if (hours > 0) result += `${hours}ч `;
  if (minutes > 0 || hours > 0) result += `${minutes}м `; // Показываем минуты, если есть часы
  result += `${seconds}с`;
  
  return result.trim();
}

/**
 * Форматирует общее количество минут в строку "Xч Yм"
 * @param {number} totalMinutes - Общее количество минут
 */
function formatMinutes(totalMinutes) {
    // 1. Обрабатываем некорректные данные
    if (isNaN(totalMinutes) || totalMinutes < 0) {
      return 'н/д';
    }
    
    // 2. Округляем до целого, чтобы избавиться от долей минуты
    const roundedTotalMinutes = Math.round(totalMinutes);

    // 3. Если после округления получилось 0, значит, это менее 1 минуты
    if (roundedTotalMinutes === 0) {
      return 'менее 1 мин.';
    }

    // 4. Вычисляем часы и минуты
    const hours = Math.floor(roundedTotalMinutes / 60);
    const minutes = roundedTotalMinutes % 60;

    // 5. Собираем результат в массив, чтобы избежать лишних пробелов
    const parts = [];
    if (hours > 0) {
      parts.push(`${hours}ч`);
    }
    if (minutes > 0) {
      parts.push(`${minutes}м`);
    }

    // 6. Объединяем части через пробел. Если массив пуст (маловероятно), возвращаем базовое значение.
    return parts.join(' ') || 'менее 1 мин.';
}


// --- Основной компонент ---

export default function TotalVisitDuration({ visitInfo }) {
  const { 
    visit_date: visitStartDate, // Дата начала всего визита
    is_quality_checked: isFinalized, 
    visit_duration_minutes: finalDurationMinutes 
  } = visitInfo;

  const [liveDuration, setLiveDuration] = useState('');

  // Хук для обновления таймера в реальном времени
  useEffect(() => {
    // Не запускаем таймер, если визит завершен или нет даты начала
    if (isFinalized || !visitStartDate) {
      return;
    }

    // Функция, которая считает и устанавливает текущую продолжительность
    const calculateAndSetDuration = () => {
       const start = new Date(visitStartDate);
       const now = new Date();
       const diffMs = now.getTime() - start.getTime();
       setLiveDuration(formatDuration(diffMs));
    };

    calculateAndSetDuration(); // Вызываем сразу для первого отображения
    const intervalId = setInterval(calculateAndSetDuration, 1000); // Обновляем каждую секунду

    // Функция очистки: остановит интервал, когда компонент размонтируется
    return () => clearInterval(intervalId); 

  }, [isFinalized, visitStartDate]); // Зависимости хука


  if (!visitStartDate) {
    return null; // Не отображаем блок, если нет даты начала визита
  }

  return (
    <Box sx={{
      mb: 2,
      p: 2,
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 1,
      bgcolor: 'background.default',
    }}>
      <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'medium', color: 'text.primary', display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <TimerOutlined />
        Время работы:
      </Typography>
      
      <Grid container alignItems="center" justifyContent="center">
        {isFinalized ? (
            // --- Отображение для ЗАВЕРШЕННОГО визита ---
            <Chip 
              icon={<CheckCircleOutline />}
              label={formatMinutes(finalDurationMinutes)} 
              color="primary" 
              variant="filled"
              sx={{ fontSize: '1.1rem', py: 2.5, px: 1.5 }}
            />
        ) : (
            // --- Отображение для АКТИВНОГО визита (живой таймер) ---
            <Chip 
              label={liveDuration} 
              color="secondary" 
              variant="filled"
              sx={{ 
                fontSize: '1.1rem', 
                py: 2.5, 
                px: 1.5,
                fontVariantNumeric: 'tabular-nums' // Для предотвращения "прыгания" цифр
              }} 
            />
        )}
      </Grid>
    </Box>
  );
}
